import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import s from "./groupLeaderboardChart.module.css";
import { personDisplayName } from "../../../utils/clientAppUtils";

function shortName(name, max = 16) {
  const t = String(name || "").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function barFill(row, accent) {
  if (row.is_me) return accent || "#7c3aed";
  if (row.rank === 1) return "#eab308";
  if (row.rank === 2) return "#94a3b8";
  if (row.rank === 3) return "#d97706";
  return accent ? `${accent}88` : "#a78bfa";
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className={s.tooltip}>
      <strong>
        {d.rank}. {personDisplayName(d.full_name) || d.full_name}
        {d.is_me ? " (вы)" : ""}
      </strong>
      <span>Явка: {d.pct != null ? `${d.pct}%` : "—"}</span>
      <span>
        {d.present} посещений из {d.marked} отмеченных
      </span>
      {d.streak >= 2 ? <span>Серия: {d.streak}</span> : null}
    </div>
  );
}

export default function GroupLeaderboardChart({
  rows = [],
  accent,
  maxBars = 12,
  title = "График явки",
}) {
  const clean = rows.filter((r) => !r.separator && r.pct != null);

  const { chartData, avgPct, height } = useMemo(() => {
    const top = clean.slice(0, maxBars);
    const avg =
      top.length > 0
        ? Math.round(
            top.reduce((sum, r) => sum + (Number(r.pct) || 0), 0) / top.length,
          )
        : 0;
    const data = top
      .map((row) => ({
        ...row,
        shortName: shortName(personDisplayName(row.full_name) || row.full_name),
        fill: barFill(row, accent),
        pct: Number(row.pct) || 0,
      }))
      .reverse();
    const h = Math.max(200, Math.min(420, data.length * 40 + 48));
    return { chartData: data, avgPct: avg, height: h };
  }, [clean, maxBars, accent]);

  if (!chartData.length) return null;

  return (
    <section
      className={s.card}
      style={{ "--lb-accent": accent || "#7c3aed" }}
      aria-label={title}
    >
      <div className={s.cardHead}>
        <div>
          <h3 className={s.cardTitle}>{title}</h3>
          <p className={s.cardHint}>Явка за период · чем выше столбик, тем лучше</p>
        </div>
        <div className={s.avgBadge}>
          <span className={s.avgVal}>{avgPct}%</span>
          <span className={s.avgLbl}>среднее</span>
        </div>
      </div>

      <div className={s.chartWrap} style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
            barCategoryGap="20%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              horizontal={false}
              stroke="var(--lb-grid, rgba(0,0,0,0.06))"
            />
            <XAxis
              type="number"
              domain={[0, 100]}
              tick={{ fontSize: 11, fill: "var(--lb-muted, #64748b)" }}
              tickFormatter={(v) => `${v}%`}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="shortName"
              width={76}
              tick={{ fontSize: 12, fontWeight: 600, fill: "var(--lb-text, #0f172a)" }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={<ChartTooltip />}
              cursor={{ fill: "var(--lb-cursor, rgba(0,0,0,0.04))" }}
            />
            <ReferenceLine
              x={avgPct}
              stroke="var(--lb-accent)"
              strokeDasharray="4 4"
              strokeOpacity={0.65}
              label={{
                value: "ср.",
                position: "insideTopRight",
                fill: "var(--lb-accent)",
                fontSize: 10,
              }}
            />
            <Bar dataKey="pct" radius={[0, 8, 8, 0]} maxBarSize={22} animationDuration={500}>
              {chartData.map((row) => (
                <Cell key={row.patient_id} fill={row.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className={s.legend}>
        <span>
          <i className={s.dot} style={{ background: "#eab308" }} />
          1 место
        </span>
        <span>
          <i className={s.dot} style={{ background: "#94a3b8" }} />
          2–3
        </span>
        <span>
          <i className={s.dot} style={{ background: "var(--lb-accent)" }} />
          вы
        </span>
      </div>
    </section>
  );
}
